import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import styles from './Recettes.module.css';

interface LigneWeb {
  commandeId: number;
  dateCommande: string;
  clientNom: string;
  clientPrenom: string;
  totalCommande: number;
  subtotal: number;
  fraisLivraison: number;
  remiseCommande: number;
  produitNom: string;
  categorie: string | null;
  quantite: number;
  prixVenteTTC: number;
  prixVenteHT: number;
  prixAchatHT: number;
  prixAchatTTC: number;
  beneficeHT: number;
  pctMarge: number;
  achatManquant: boolean;
}

interface VenteManuelle {
  id: number;
  dateVente: string;
  produitNom: string;
  categorie: string | null;
  quantite: number;
  prixVenteTTC: number;
  prixAchatHT: number;
  note: string | null;
}

interface LigneUnifiee {
  source: 'web' | 'manuel';
  id: string;
  date: string;
  client: string;
  produitNom: string;
  categorie: string | null;
  quantite: number;
  prixVenteTTC: number;
  prixVenteHT: number;
  prixAchatHT: number;
  prixAchatTTC: number;
  beneficeHT: number;
  pctMarge: number;
  achatManquant: boolean;
  remiseCommande: number;
  note: string | null;
  groupId: string;
}

const TVA = 1.20;
const fmt = (n: number) => n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
const fmtPct = (n: number) => n.toFixed(1) + ' %';
const todayStr = () => new Date().toISOString().split('T')[0];
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };
const firstOfYear  = () => { const d = new Date(); d.setMonth(0, 1); return d.toISOString().split('T')[0]; };

const emptyForm = { date: todayStr(), produitNom: '', categorie: '', quantite: '1', prixVenteTTC: '', prixAchatHT: '', note: '' };

export const Recettes: React.FC = () => {
  const [lignesWeb, setLignesWeb] = useState<LigneWeb[]>([]);
  const [ventesMan, setVentesMan] = useState<VenteManuelle[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Formulaire vente manuelle
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);

  const setF = (k: keyof typeof emptyForm, v: string) => setForm(f => ({ ...f, [k]: v }));

  const loadAll = useCallback(async (from: string, to: string) => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to)   params.set('to', to);
      const qs = params.toString() ? '?' + params.toString() : '';
      const [r1, r2] = await Promise.all([
        api.get<{ success: boolean; data: LigneWeb[] }>(`recettes${qs}`),
        api.get<{ success: boolean; data: VenteManuelle[] }>(`ventesmanuelles${qs}`),
      ]);
      setLignesWeb(r1.data.data ?? []);
      setVentesMan(r2.data.data ?? []);
    } catch (e: any) {
      setError('Erreur de chargement : ' + (e.message || 'inconnue'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll('', ''); }, [loadAll]);

  const handleApply  = () => loadAll(dateFrom, dateTo);
  const setThisMonth = () => { const f = firstOfMonth(), t = todayStr(); setDateFrom(f); setDateTo(t); loadAll(f, t); };
  const setThisYear  = () => { const f = firstOfYear(),  t = todayStr(); setDateFrom(f); setDateTo(t); loadAll(f, t); };
  const setAll       = () => { setDateFrom(''); setDateTo(''); loadAll('', ''); };

  // ── Lignes unifiées ───────────────────────────────────────────────────────
  const lignes: LigneUnifiee[] = useMemo(() => {
    const web: LigneUnifiee[] = lignesWeb.map(l => ({
      source: 'web',
      id: `web-${l.commandeId}-${l.produitNom}`,
      date: l.dateCommande,
      client: `${l.clientPrenom} ${l.clientNom}`,
      produitNom: l.produitNom,
      categorie: l.categorie,
      quantite: l.quantite,
      prixVenteTTC: l.prixVenteTTC,
      prixVenteHT: l.prixVenteHT,
      prixAchatHT: l.prixAchatHT,
      prixAchatTTC: l.prixAchatTTC,
      beneficeHT: l.beneficeHT,
      pctMarge: l.pctMarge,
      achatManquant: l.achatManquant,
      remiseCommande: l.remiseCommande,
      note: null,
      groupId: `web-${l.commandeId}`,
    }));

    const man: LigneUnifiee[] = ventesMan.map(v => {
      const prixVenteHT  = v.prixVenteTTC / TVA;
      const prixAchatTTC = v.prixAchatHT * TVA;
      const beneficeHT   = (prixVenteHT - v.prixAchatHT) * v.quantite;
      const pctMarge     = prixVenteHT > 0 ? (prixVenteHT - v.prixAchatHT) / prixVenteHT * 100 : 0;
      return {
        source: 'manuel',
        id: `man-${v.id}`,
        date: v.dateVente,
        client: '(vente directe)',
        produitNom: v.produitNom,
        categorie: v.categorie,
        quantite: v.quantite,
        prixVenteTTC: v.prixVenteTTC,
        prixVenteHT,
        prixAchatHT: v.prixAchatHT,
        prixAchatTTC,
        beneficeHT,
        pctMarge,
        achatManquant: v.prixAchatHT === 0,
        remiseCommande: 0,
        note: v.note,
        groupId: `man-${v.id}`,
      };
    });

    return [...web, ...man].sort((a, b) => b.date.localeCompare(a.date));
  }, [lignesWeb, ventesMan]);

  // ── Résumé ────────────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    if (!lignes.length) return null;
    const seenWeb = new Set<number>();
    let caTTC = 0, totalRemises = 0;
    lignesWeb.forEach(l => {
      if (!seenWeb.has(l.commandeId)) {
        seenWeb.add(l.commandeId);
        caTTC        += l.totalCommande - l.fraisLivraison;
        totalRemises += l.remiseCommande;
      }
    });
    ventesMan.forEach(v => { caTTC += v.prixVenteTTC * v.quantite; });
    const caHT     = caTTC / TVA;
    const coutHT   = lignes.reduce((s, l) => s + l.prixAchatHT * l.quantite, 0);
    const benefHT  = caHT - coutHT;
    const pctMarge = caHT > 0 ? benefHT / caHT * 100 : 0;
    return { caTTC, caHT, coutHT, benefHT, pctMarge, totalRemises, nbWeb: seenWeb.size, nbMan: ventesMan.length };
  }, [lignes, lignesWeb, ventesMan]);

  // ── Gestion formulaire vente manuelle ─────────────────────────────────────
  const openNew  = () => { setEditId(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (v: VenteManuelle) => {
    setEditId(v.id);
    setForm({
      date: v.dateVente.split('T')[0],
      produitNom: v.produitNom,
      categorie: v.categorie ?? '',
      quantite: String(v.quantite),
      prixVenteTTC: String(v.prixVenteTTC),
      prixAchatHT: String(v.prixAchatHT),
      note: v.note ?? '',
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.produitNom.trim()) { alert('Nom du produit obligatoire.'); return; }
    if (!form.prixVenteTTC)      { alert('Prix de vente TTC obligatoire.'); return; }
    const dto = {
      dateVente: form.date,
      produitNom: form.produitNom.trim(),
      categorie: form.categorie || null,
      quantite: parseInt(form.quantite) || 1,
      prixVenteTTC: parseFloat(form.prixVenteTTC.replace(',', '.')) || 0,
      prixAchatHT:  parseFloat(form.prixAchatHT.replace(',', '.'))  || 0,
      note: form.note || null,
    };
    try {
      if (editId !== null) {
        await api.put(`ventesmanuelles/${editId}`, dto);
      } else {
        await api.post('ventesmanuelles', dto);
      }
      setShowForm(false);
      loadAll(dateFrom, dateTo);
    } catch (e: any) {
      alert('Erreur : ' + (e.response?.data?.message || e.message));
    }
  };

  const handleDelete = async (id: number, nom: string) => {
    if (!window.confirm(`Supprimer la vente « ${nom} » ?`)) return;
    try {
      await api.delete(`ventesmanuelles/${id}`);
      loadAll(dateFrom, dateTo);
    } catch (e: any) {
      alert('Erreur : ' + e.message);
    }
  };

  // ── Séparateur de groupe ───────────────────────────────────────────────────
  const lignesAvecGroupe = useMemo(() => {
    let lastGroup = '';
    return lignes.map(l => {
      const isNew = l.groupId !== lastGroup;
      lastGroup = l.groupId;
      return { ...l, isNew };
    });
  }, [lignes]);

  return (
    <Layout>
      <div className={styles.container}>
        <h1 className={styles.title}>Recettes & Rentabilité</h1>

        {error && <div className={styles.error}>{error}</div>}

        {/* ── Filtres ──────────────────────────────────────────────────── */}
        <div className={styles.filterBar}>
          <span className={styles.filterLabel}>Du</span>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={styles.filterInput} />
          <span className={styles.filterLabel}>au</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={styles.filterInput} />
          <button onClick={handleApply}  className={styles.btnFilter}>Appliquer</button>
          <button onClick={setThisMonth} className={styles.btnShortcut}>Ce mois</button>
          <button onClick={setThisYear}  className={styles.btnShortcut}>Cette année</button>
          <button onClick={setAll}       className={styles.btnShortcut}>Tout</button>
          <button onClick={openNew}      className={styles.btnAdd}>+ Vente manuelle</button>
        </div>

        {/* ── Formulaire vente manuelle ─────────────────────────────────── */}
        {showForm && (
          <div className={styles.manForm}>
            <h3 className={styles.manFormTitle}>{editId ? 'Modifier la vente' : 'Nouvelle vente manuelle'}</h3>
            <div className={styles.manFormGrid}>
              <div className={styles.manField}>
                <label>Date *</label>
                <input type="date" value={form.date} onChange={e => setF('date', e.target.value)} className={styles.filterInput} />
              </div>
              <div className={styles.manField}>
                <label>Produit *</label>
                <input type="text" value={form.produitNom} onChange={e => setF('produitNom', e.target.value)} placeholder="Nom du produit" className={styles.manInput} />
              </div>
              <div className={styles.manField}>
                <label>Catégorie</label>
                <input type="text" value={form.categorie} onChange={e => setF('categorie', e.target.value)} placeholder="E-Liquide, DIY…" className={styles.manInput} />
              </div>
              <div className={styles.manField}>
                <label>Qté *</label>
                <input type="number" value={form.quantite} onChange={e => setF('quantite', e.target.value)} min="1" className={styles.manInput} />
              </div>
              <div className={styles.manField}>
                <label>Prix vente TTC *</label>
                <input type="text" inputMode="decimal" value={form.prixVenteTTC} onChange={e => setF('prixVenteTTC', e.target.value)} placeholder="0,00" className={styles.manInput} />
              </div>
              <div className={styles.manField}>
                <label>Prix achat HT</label>
                <input type="text" inputMode="decimal" value={form.prixAchatHT} onChange={e => setF('prixAchatHT', e.target.value)} placeholder="0,00" className={styles.manInput} />
              </div>
              <div className={styles.manField} style={{ gridColumn: 'span 2' }}>
                <label>Note</label>
                <input type="text" value={form.note} onChange={e => setF('note', e.target.value)} placeholder="Ex: vente en foire, client particulier…" className={styles.manInput} />
              </div>
            </div>
            <div className={styles.manFormActions}>
              <button onClick={handleSave} className={styles.btnFilter}>Enregistrer</button>
              <button onClick={() => setShowForm(false)} className={styles.btnShortcut}>Annuler</button>
            </div>
          </div>
        )}

        {/* ── Cartes résumé ────────────────────────────────────────────── */}
        {summary && (
          <div className={styles.summaryGrid}>
            <div className={styles.card}>
              <div className={styles.cardLabel}>Commandes web</div>
              <div className={styles.cardValue}>{summary.nbWeb}</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardLabel}>Ventes manuelles</div>
              <div className={styles.cardValue}>{summary.nbMan}</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardLabel}>CA TTC total</div>
              <div className={styles.cardValue}>{fmt(summary.caTTC)}</div>
            </div>
            <div className={styles.card}>
              <div className={styles.cardLabel}>CA HT total</div>
              <div className={styles.cardValue}>{fmt(summary.caHT)}</div>
            </div>
            <div className={`${styles.card} ${styles.cardRed}`}>
              <div className={styles.cardLabel}>Coût d'achat HT</div>
              <div className={styles.cardValue}>{fmt(summary.coutHT)}</div>
            </div>
            <div className={`${styles.card} ${styles.cardGreen}`}>
              <div className={styles.cardLabel}>Bénéfice net HT</div>
              <div className={styles.cardValue}>{fmt(summary.benefHT)}</div>
            </div>
            <div className={`${styles.card} ${styles.cardGreen}`}>
              <div className={styles.cardLabel}>Marge moyenne</div>
              <div className={styles.cardValue}>{fmtPct(summary.pctMarge)}</div>
            </div>
            {summary.totalRemises > 0 && (
              <div className={`${styles.card} ${styles.cardOrange}`}>
                <div className={styles.cardLabel}>Remises promo</div>
                <div className={styles.cardValue}>−{fmt(summary.totalRemises)}</div>
              </div>
            )}
          </div>
        )}

        {/* ── Tableau ──────────────────────────────────────────────────── */}
        <div className={styles.tableWrap}>
          {loading ? (
            <div className={styles.loading}>Chargement…</div>
          ) : lignes.length === 0 ? (
            <div className={styles.noData}>Aucune vente sur cette période.</div>
          ) : (
            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>Source</th>
                    <th>Date</th>
                    <th>Client / Note</th>
                    <th>Produit</th>
                    <th>Catégorie</th>
                    <th>Qté</th>
                    <th>Achat HT</th>
                    <th>Achat TTC</th>
                    <th>Vente HT</th>
                    <th>Vente TTC</th>
                    <th>Remise</th>
                    <th>Bénéf. HT</th>
                    <th>% Marge</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lignesAvecGroupe.map((l, i) => {
                    const vm = l.source === 'manuel' ? ventesMan.find(v => `man-${v.id}` === l.id) : null;
                    return (
                      <tr key={i} className={l.isNew ? styles.rowGroupStart : ''}>
                        <td>
                          <span style={{
                            display: 'inline-block', padding: '1px 6px', borderRadius: 4,
                            fontSize: '0.75rem', fontWeight: 600,
                            background: l.source === 'web' ? '#e3f2fd' : '#e8f5e9',
                            color: l.source === 'web' ? '#1565c0' : '#2e7d32',
                          }}>
                            {l.source === 'web' ? 'Web' : 'Manuel'}
                          </span>
                        </td>
                        <td>{new Date(l.date).toLocaleDateString('fr-FR')}</td>
                        <td style={{ fontSize: '0.8rem', color: '#555' }}>
                          {l.source === 'web' ? l.client : (l.note || '(vente directe)')}
                        </td>
                        <td>{l.achatManquant ? <span className={styles.warning}>{l.produitNom} ⚠</span> : l.produitNom}</td>
                        <td>{l.categorie ?? '—'}</td>
                        <td>{l.quantite}</td>
                        <td>{l.achatManquant ? <span className={styles.warning}>—</span> : fmt(l.prixAchatHT)}</td>
                        <td>{l.achatManquant ? '—' : fmt(l.prixAchatTTC)}</td>
                        <td>{fmt(l.prixVenteHT)}</td>
                        <td>{fmt(l.prixVenteTTC)}</td>
                        <td>
                          {l.remiseCommande > 0
                            ? <span style={{ color: '#f39c12', fontWeight: 600 }}>−{fmt(l.remiseCommande)}</span>
                            : '—'}
                        </td>
                        <td className={l.beneficeHT >= 0 ? styles.positif : styles.negatif}>
                          {l.achatManquant ? '—' : fmt(l.beneficeHT)}
                        </td>
                        <td className={l.pctMarge >= 0 ? styles.positif : styles.negatif}>
                          {l.achatManquant ? '—' : fmtPct(l.pctMarge)}
                        </td>
                        <td>
                          {l.source === 'manuel' && vm && (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button onClick={() => openEdit(vm)} className={styles.btnShortcut} style={{ padding: '2px 8px', fontSize: '0.75rem' }}>✏</button>
                              <button onClick={() => handleDelete(vm.id, vm.produitNom)} className={styles.btnShortcut} style={{ padding: '2px 8px', fontSize: '0.75rem', color: '#e74c3c' }}>✕</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {lignes.some(l => l.achatManquant) && (
          <p style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#f39c12' }}>
            ⚠ Les produits marqués n'ont pas de prix d'achat renseigné.
          </p>
        )}
      </div>
    </Layout>
  );
};
